/**
 * RichTextContent — render arbitrary stored content (HTML, Markdown, or plain
 * text) safely and consistently, with optional "Show more / Show less" clamping.
 *
 * Why this exists: descriptions/notes/summaries across the app (courses, KB
 * articles, sessions…) are authored in different editors over time, so a single
 * field may hold raw HTML, Markdown, or plain text. Rendering one format as
 * another either shows raw tags/asterisks or — when dumped into a bare
 * `dangerouslySetInnerHTML` with no typography styles — renders lists/headings
 * unstyled so the formatting looks stripped. This component:
 *   1. detects the format and converts to sanitized HTML (marked + DOMPurify),
 *   2. styles it via Mantine's TypographyStylesProvider (proper lists/headings/links),
 *   3. auto-links bare URLs and email addresses,
 *   4. optionally clamps to N lines (with a trailing ellipsis) behind a
 *      right-aligned Show more / Show less toggle. The toggle only appears when
 *      the content actually overflows — shorter content just wraps naturally.
 *
 * Reuse it anywhere a stored description/notes/summary string is displayed.
 */

import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { TypographyStylesProvider, Anchor } from '@mantine/core';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { Text as UIText } from '@/components/ui';

marked.setOptions({ gfm: true, breaks: true });

export interface RichTextContentProps {
  /** Stored content — may be HTML, Markdown, or plain text. */
  content?: string | null;
  /**
   * Collapse to roughly this many lines with a "Show more" toggle. Pass 0 to
   * disable clamping and always render the full content. Default 4.
   */
  lineClamp?: number;
  /** Auto-link bare URLs and email addresses found in text. Default true. */
  linkify?: boolean;
  /** Base font size in px. Default 14. */
  fontSize?: number;
  /** Shown (dimmed, italic) when content is empty. Omit to render nothing. */
  emptyText?: string;
  /**
   * @deprecated No longer used — the collapsed state now ends with a real
   * ellipsis instead of a gradient fade. Kept so existing callers type-check.
   */
  fadeColor?: string;
  className?: string;
  style?: React.CSSProperties;
}

const HTML_TAG_RE = /<\/?[a-z][\s\S]*>/i;

function looksLikeHtml(s: string): boolean {
  return HTML_TAG_RE.test(s);
}

/** Fresh regex per call — `g` flag carries lastIndex state, so never share. */
function newLinkRe(): RegExp {
  return /(https?:\/\/[^\s<]+[^\s<.,:;!?)'"\]]|www\.[^\s<]+[^\s<.,:;!?)'"\]]|[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,})/g;
}

function hrefFor(match: string): string {
  if (/^https?:\/\//i.test(match)) return match;
  if (/^www\./i.test(match)) return `https://${match}`;
  return `mailto:${match}`;
}

const SKIP_TAGS = new Set(['A', 'CODE', 'PRE', 'SCRIPT', 'STYLE', 'TEXTAREA', 'BUTTON']);

function isInsideSkipped(el: Element | null, root: Element): boolean {
  let cur: Element | null = el;
  while (cur && cur !== root) {
    if (SKIP_TAGS.has(cur.tagName)) return true;
    cur = cur.parentElement;
  }
  return false;
}

/**
 * Harden every existing anchor (target/rel) and wrap bare URLs/emails — that
 * aren't already inside a link or code block — in anchors. Operates only on
 * already-sanitized HTML and only ever creates http/https/mailto links.
 */
function linkifyHtml(html: string): string {
  if (typeof window === 'undefined' || !('DOMParser' in window)) return html;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const root = doc.body;

  root.querySelectorAll('a[href]').forEach((a) => {
    a.setAttribute('target', '_blank');
    a.setAttribute('rel', 'noopener noreferrer nofollow');
  });

  // Collect first — mutating the tree mid-walk invalidates the walker.
  const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const targets: Text[] = [];
  let node = walker.nextNode();
  while (node) {
    const tn = node as Text;
    if (tn.nodeValue && tn.nodeValue.trim() && !isInsideSkipped(tn.parentElement, root)) {
      if (newLinkRe().test(tn.nodeValue)) targets.push(tn);
    }
    node = walker.nextNode();
  }

  for (const tn of targets) {
    const text = tn.nodeValue ?? '';
    const re = newLinkRe();
    const frag = doc.createDocumentFragment();
    let last = 0;
    let m: RegExpExecArray | null;
    while ((m = re.exec(text)) !== null) {
      const [match] = m;
      if (m.index > last) frag.appendChild(doc.createTextNode(text.slice(last, m.index)));
      const a = doc.createElement('a');
      a.setAttribute('href', hrefFor(match));
      a.setAttribute('target', '_blank');
      a.setAttribute('rel', 'noopener noreferrer nofollow');
      a.textContent = match;
      frag.appendChild(a);
      last = m.index + match.length;
    }
    if (last < text.length) frag.appendChild(doc.createTextNode(text.slice(last)));
    tn.parentNode?.replaceChild(frag, tn);
  }

  return root.innerHTML;
}

function toSafeHtml(content: string, linkify: boolean): string {
  const raw = looksLikeHtml(content)
    ? content
    : (marked.parse(content, { async: false }) as string);
  const clean = DOMPurify.sanitize(raw, { ADD_ATTR: ['target', 'rel'] });
  return linkify ? linkifyHtml(clean) : clean;
}

export function RichTextContent({
  content,
  lineClamp = 4,
  linkify = true,
  fontSize = 14,
  emptyText,
  className,
  style,
}: RichTextContentProps) {
  const html = useMemo(() => {
    const trimmed = content?.trim();
    if (!trimmed) return '';
    return toSafeHtml(trimmed, linkify);
  }, [content, linkify]);

  const ref = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [overflowing, setOverflowing] = useState(false);

  const clampEnabled = lineClamp > 0;

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || !clampEnabled) {
      setOverflowing(false);
      return;
    }
    const measure = () => {
      const cs = window.getComputedStyle(el);
      let lh = parseFloat(cs.lineHeight);
      if (Number.isNaN(lh)) lh = parseFloat(cs.fontSize || String(fontSize)) * 1.6;
      // el is never itself clamped (the clamp lives on its parent), so
      // scrollHeight always reflects the full, unclipped content height.
      setOverflowing(el.scrollHeight > Math.round(lh * lineClamp) + 2);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [html, lineClamp, clampEnabled, fontSize]);

  if (!html) {
    return emptyText ? (
      <UIText c="dimmed" fs="italic" size="sm">
        {emptyText}
      </UIText>
    ) : null;
  }

  const collapsed = clampEnabled && overflowing && !expanded;

  return (
    <div className={className} style={style}>
      <div
        style={
          collapsed
            ? {
                // Real line clamp: shows exactly `lineClamp` lines and ends the
                // last visible line with an ellipsis, so truncation is obvious.
                display: '-webkit-box',
                WebkitLineClamp: lineClamp,
                WebkitBoxOrient: 'vertical' as const,
                overflow: 'hidden',
              }
            : undefined
        }
      >
        <TypographyStylesProvider style={{ fontSize }}>
          <div ref={ref} dangerouslySetInnerHTML={{ __html: html }} />
        </TypographyStylesProvider>
      </div>
      {clampEnabled && overflowing && (
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Anchor
            component="button"
            type="button"
            size="sm"
            fw={600}
            onClick={() => setExpanded((e) => !e)}
            style={{ marginTop: 6 }}
          >
            {expanded ? 'Show less' : 'Show more'}
          </Anchor>
        </div>
      )}
    </div>
  );
}

export default RichTextContent;
